/**
 * Formats error objects from various sources (GraphQL, RTK Query, standard errors)
 * into user-friendly error messages
 */
export const formatError = (error: any): string => {
    // Handle GraphQL errors (response.errors array)
    if (error?.response?.errors && Array.isArray(error.response.errors)) {
        const errorMessages = error.response.errors.map((err: any) => {
            let message = err.message || 'Unknown error';

            // Add path information if available (e.g., ["variable", "input", "features"])
            if (err.path && Array.isArray(err.path) && err.path.length > 0) {
                // Filter out "variable" and "input" from path to show the actual field
                const relevantPath = err.path.filter((p: string) => p !== 'variable' && p !== 'input');
                if (relevantPath.length > 0) {
                    const fieldName = relevantPath.join('.');
                    message = `${message} (field: ${fieldName})`;
                }
            }

            // Add error code if it's a validation error
            if (err.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') {
                message = `Validation error: ${message}`;
            }

            return message;
        });

        return errorMessages.join('\n');
    }

    // Handle RTK Query errors (error.data)
    if (error?.data?.message) {
        return error.data.message;
    }

    // Handle standard error messages
    if (error?.message) {
        return error.message;
    }

    // Handle error.error
    if (error?.error) {
        return typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
    }

    // Fallback
    return 'An error occurred. Please try again.';
};

/**
 * Converts RTK Query / custom base-query error objects into a standard `Error`
 * for components that expect one (e.g. error boundaries, ErrorScreen).
 */
export const rtkQueryErrorToError = (error: unknown, fallbackMessage = 'An error occurred. Please try again.'): Error => {
    if (error instanceof Error) {
        return error;
    }
    if (typeof error === 'object' && error !== null) {
        const e = error as Record<string, unknown>;
        if (typeof e.message === 'string' && e.message) {
            return new Error(e.message);
        }
        if (e.status !== undefined) {
            const status = String(e.status);
            const data = e.data;
            const detail = typeof data === 'string' ? data : JSON.stringify(data ?? '');
            return new Error(`${status}: ${detail}`);
        }
    }
    return new Error(fallbackMessage);
};
